import { computed, inject, Injectable } from '@angular/core';
import { SpriteSheetService, TSprite } from '@features/styles/sprite-sheet.service';

export type { TSprite } from '@features/styles/sprite-sheet.service';

export type TText = {
  color: string;
  bold?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class StylesService {
  readonly #spriteSheetService = inject(SpriteSheetService);

  // STORY
  public readonly STORY_PANEL_MAIN = computed<{ container: TSprite; text: TText } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_PANEL_PRIMARY();
    return container ? { container: { ...container, scale: 0.5 }, text: { color: 'white' } } : undefined;
  });

  public readonly STORY_PANEL_TITLE = computed<{ container: TSprite; text: TText } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_HEADER_PRIMARY();
    return container ? { container: { ...container, scale: 0.25 }, text: { color: 'white', bold: true } } : undefined;
  });

  // INVENTORY
  public readonly INVENTORY_PANEL = computed<{ container: TSprite; text: TText } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_PANEL_INVENTORY();
    return container ? { container: { ...container, scale: 0.25 }, text: { color: 'black', bold: false } } : undefined;
  });

  public readonly INVENTORY_SLOT = computed<{ container: TSprite; text: TText } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_PANEL_SLOT();
    return container ? { container: { ...container, scale: 0.25 }, text: { color: 'black', bold: false } } : undefined;
  });

  public readonly INVENTORY_SLOT_HIGHLIGHTED = computed<{ container: TSprite; text: TText } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_PANEL_SLOT_HIGHLIGHTED();
    return container ? { container: { ...container, scale: 0.25 }, text: { color: 'black', bold: false } } : undefined;
  });

  public readonly INVENTORY_TAB_ACTIVE = computed<{ container: TSprite; text: TText } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_TAB_OPEN();
    return container ? { container: { ...container, scale: 0.25 }, text: { color: 'white', bold: true } } : undefined;
  });

  public readonly INVENTORY_TAB_INACTIVE = computed<{ container: TSprite; text: TText } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_TAB_NORMAL();
    return container ? { container: { ...container, scale: 0.25 }, text: { color: 'black', bold: false } } : undefined;
  });

  // HUD
  public readonly HUD_BUTTON_PRIMARY = computed<{ container: TSprite; text: TText } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_BUTTON_PRIMARY();
    return container ? { container: { ...container, scale: 0.25 }, text: { color: 'black', bold: false } } : undefined;
  });

  public readonly HUD_BUTTON_SECONDARY = computed<{ container: TSprite; text: TText } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_BUTTON_SECONDARY();
    return container ? { container: { ...container, scale: 0.25 }, text: { color: 'black', bold: false } } : undefined;
  });

  public readonly HUD_BUTTON_SUCCESS = computed<{ container: TSprite; text: TText } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_BUTTON_SUCCESS();
    return container ? { container: { ...container, scale: 0.25 }, text: { color: 'white', bold: false } } : undefined;
  });

  public readonly HUD_BUTTON_DANGER = computed<{ container: TSprite; text: TText } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_BUTTON_DANGER();
    return container ? { container: { ...container, scale: 0.25 }, text: { color: 'white', bold: false } } : undefined;
  });

  public readonly HUD_BUTTON_DISABLED = computed<{ container: TSprite; text: TText } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_BUTTON_DISABLED();
    return container ? { container: { ...container, scale: 0.25 }, text: { color: '#4b525a', bold: false } } : undefined;
  });

  // NOTIFICATION
  public readonly HUD_NOTIFICATION_PANEL = computed<{ container: TSprite; text: TText } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_PANEL_PRIMARY();
    return container ? { container: { ...container, scale: 0.5 }, text: { color: 'white' } } : undefined;
  });

  // LABELS
  public readonly LABEL_PANEL = computed<{ container: TSprite; text: TText } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_PANEL_SECONDARY();
    return container ? { container: { ...container, scale: 0.5 }, text: { color: 'white' } } : undefined;
  });

  // RAW SPRITES
  public readonly SPRITE_BUTTON_PRIMARY = computed<{ container: TSprite } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_BUTTON_PRIMARY();
    return container ? { container } : undefined;
  });

  public readonly SPRITE_BUTTON_PRIMARY_ROUND = computed<{ container: TSprite } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_BUTTON_PRIMARY_ROUND();
    return container ? { container } : undefined;
  });

  public readonly SPRITE_BUTTON_SECONDARY = computed<{ container: TSprite } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_BUTTON_SECONDARY();
    return container ? { container } : undefined;
  });

  public readonly SPRITE_BUTTON_DISABLED = computed<{ container: TSprite } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_BUTTON_DISABLED();
    return container ? { container } : undefined;
  });

  public readonly SPRITE_BUTTON_ICON = computed<{ container: TSprite } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_BUTTON_ICON();
    return container ? { container } : undefined;
  });

  public readonly SPRITE_BUTTON_ICON_HIGHLIGHT = computed<{ container: TSprite } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_BUTTON_ICON_HIGHLIGHT();
    return container ? { container } : undefined;
  });

  public readonly SPRITE_TAB_NORMAL = computed<{ container: TSprite } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_TAB_NORMAL();
    return container ? { container } : undefined;
  });

  public readonly SPRITE_TAB_OPEN = computed<{ container: TSprite } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_TAB_OPEN();
    return container ? { container } : undefined;
  });

  public readonly SPRITE_PANEL_PRIMARY = computed<{ container: TSprite } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_PANEL_PRIMARY();
    return container ? { container } : undefined;
  });

  public readonly SPRITE_PANEL_SECONDARY = computed<{ container: TSprite } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_PANEL_SECONDARY();
    return container ? { container } : undefined;
  });

  public readonly SPRITE_SCROLLBAR_BACKGROUND = computed<{ container: TSprite } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_SCROLLBAR_BACKGROUND();
    return container ? { container } : undefined;
  });

  public readonly SPRITE_SCROLLBAR_HANDLE = computed<{ container: TSprite } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_SCROLLBAR_HANDLE();
    return container ? { container } : undefined;
  });

  public readonly SPRITE_ICON_INVENTORY = computed<{ container: TSprite } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_ICON_INVENTORY();
    return container ? { container } : undefined;
  });

  public readonly SPRITE_ICON_CROSS = computed<{ container: TSprite } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_ICON_CROSS();
    return container ? { container } : undefined;
  });

  public readonly SPRITE_ICON_ARROW = computed<{ container: TSprite } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_ICON_ARROW();
    return container ? { container } : undefined;
  });

  public readonly SPRITE_PANEL_SLOT = computed<{ container: TSprite } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_PANEL_SLOT();
    return container ? { container } : undefined;
  });

  public readonly SPRITE_PANEL_SLOT_HIGHLIGHTED = computed<{ container: TSprite } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_PANEL_SLOT_HIGHLIGHTED();
    return container ? { container } : undefined;
  });

  public readonly SPRITE_PANEL_INVENTORY = computed<{ container: TSprite } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_PANEL_INVENTORY();
    return container ? { container } : undefined;
  });

  public readonly SPRITE_HEADER_PRIMARY = computed<{ container: TSprite } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_HEADER_PRIMARY();
    return container ? { container } : undefined;
  });

  // WOORDKR8
  public readonly WK8_TUTOR_PANEL = computed<{ container: TSprite; text: TText } | undefined>(() => {
    const container = this.#spriteSheetService.SPRITE_PANEL_SECONDARY();
    return container ? { container: { ...container, scale: 0.5 }, text: { color: 'white' } } : undefined;
  });
}
